import { Button } from '@base-ui/react/button';
import {
  faBookOpen,
  faFileLines,
  faImage,
  faLockOpen,
} from '@fortawesome/free-solid-svg-icons';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Chip,
  ChipList,
  ContentPicker,
  type PickedContent,
} from '@/admin/content/ContentPicker';
import { CoursePackageExportButton } from '@/admin/content/CoursePackageExportButton';
import { CoursePackageImportButton } from '@/admin/content/CoursePackageImportDialog';
import {
  COURSE_SETTINGS_REFUSALS,
  refusalKey,
} from '@/admin/content/contentErrors';
import { Frame, MediaPicker } from '@/admin/content/MediaPicker';
import { TranslationLinksPanel } from '@/admin/content/TranslationLinksPanel';
import { apiErrorDetail } from '@/admin/errorDetail';
import buttonStyles from '@/admin/styles/button.module.css';
import { ConfirmDialog } from '@/admin/ui/ConfirmDialog';
import { CourseHeader, CourseOutlineLayout } from '@/admin/ui/ContentNav';
import { DeleteContent } from '@/admin/ui/DeleteContent';
import { DraftAncestorNotice } from '@/admin/ui/DraftAncestorNotice';
import {
  DetailPage,
  EditorLayout,
  EditorMain,
  EditorPanel,
  FormSection,
  PanelGroup,
  PanelLabel,
  SaveGroup,
} from '@/admin/ui/Editor';
import { useEditMode, validateEditSearch } from '@/admin/ui/editSearch';
import { Field, FieldLabel, FieldValue } from '@/admin/ui/FieldDisplay';
import {
  CheckboxLabel,
  ErrorText,
  FieldHint,
  FormLabel,
  FormRow,
  InfoNotice,
  Select,
  TextInput,
  WarningNotice,
} from '@/admin/ui/Form';
import { RichContent } from '@/admin/ui/RichContent';
import { RichTextEditor } from '@/admin/ui/RichTextEditor';
import { FieldGrid, Section, SectionTitle } from '@/admin/ui/Section';
import { useUnsavedChanges } from '@/admin/ui/useUnsavedChanges';
import {
  EditConflictNotice,
  editConflict,
  invalidateCourseVersions,
  LiveEditNotice,
} from '@/admin/ui/Versions';
import { CertificateTemplateControl } from '@/admin/widgets/CertificateTemplateControl';
import api, { queryClient } from '@/api';
import type { components } from '@/api/qraft/schema';

type CourseAdmin = components['schemas']['CourseAdmin'];

function invalidateCourse(courseId: number, version: number) {
  queryClient.invalidateQueries({
    queryKey: api.adminGetCourse.getQueryKey({
      path: { course_id: courseId },
      query: { version },
    }),
  });
  queryClient.invalidateQueries({
    queryKey: api.adminListCourses.getQueryKey({}),
  });
}

export const Route = createFileRoute('/admin/courses/$courseId/$version')({
  staticData: { minTier: 'ADMIN' },
  component: CourseDetail,
  validateSearch: validateEditSearch,
});

function CourseDetail() {
  const { t } = useTranslation();
  const { courseId, version } = Route.useParams();
  const courseIdNum = Number(courseId);
  const versionNum = Number(version);
  const { edit } = Route.useSearch();
  const { editing, startEditing, stopEditing } = useEditMode(edit === true);

  // Admin-only read: the student course_detail is gated to a fully published
  // path, so a draft course would 404 there.
  const admin = api.adminGetCourse.useQuery({
    path: { course_id: courseIdNum },
    query: { version: versionNum },
  });

  if (admin.isLoading)
    return <Message>{t('admin.courses.loadingOne')}</Message>;
  if (admin.error || !admin.data)
    return <Message $error>{t('admin.courses.errorLoadingOne')}</Message>;

  const course = admin.data;
  const certificateTitle = course.certificate_template_title;

  return (
    <DetailPage>
      <CourseHeader
        course={course}
        nav={!editing}
        action={
          !editing && (
            <HeaderActions>
              <CoursePackageExportButton
                courseId={course.id}
                version={course.version}
              />
              <CoursePackageImportButton
                target={{ type: 'course', id: course.id }}
                label={t('admin.content.packages.importAsDraftButton')}
              />
              <Button className={buttonStyles.Button} onClick={startEditing}>
                {t('admin.common.edit')}
              </Button>
            </HeaderActions>
          )
        }
      />

      {editing ? (
        <CourseEditor
          key={`${course.id}-${course.version}`}
          course={course}
          onDone={stopEditing}
        />
      ) : (
        <CourseOutlineLayout course={course}>
          <Section>
            <SectionTitle>{t('admin.common.overview')}</SectionTitle>
            <FieldGrid>
              <Field>
                <FieldLabel>{t('admin.common.id')}</FieldLabel>
                <FieldValue>{course.id}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.courses.version')}</FieldLabel>
                <FieldValue>{course.version}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.common.language')}</FieldLabel>
                <FieldValue>{course.language ?? '—'}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.common.slug')}</FieldLabel>
                <FieldValue>{course.slug || '—'}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.common.order')}</FieldLabel>
                <FieldValue>{course.order}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.common.status')}</FieldLabel>
                <FieldValue>{course.status}</FieldValue>
              </Field>
            </FieldGrid>
          </Section>

          <Section>
            <SectionTitle>{t('admin.courses.access')}</SectionTitle>
            <FieldGrid>
              <YesNo
                label={t('admin.courses.studyHelperRequired')}
                value={course.study_helper_required}
              />
              <YesNo
                label={t('admin.courses.unlockedByStudyHelper')}
                value={course.unlocked_by_study_helper}
              />
              <YesNo
                label={t('admin.courses.unlockedNotRestricted')}
                value={course.unlocked_not_restricted}
              />
              <YesNo
                label={t('admin.courses.isIntro')}
                value={course.is_intro}
              />
              <Field>
                <FieldLabel>{t('admin.courses.accessMode')}</FieldLabel>
                <FieldValue>
                  {course.access_mode === 'closed'
                    ? t('admin.courses.accessClosed')
                    : t('admin.courses.accessOpen')}
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.courses.prerequisite')}</FieldLabel>
                <FieldValue>
                  {course.prerequisite_course?.title ?? '—'}
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.courses.requiredSeries')}</FieldLabel>
                <FieldValue>
                  {course.series_completion_required.length > 0
                    ? course.series_completion_required
                        .map((r) => r.title)
                        .join(', ')
                    : '—'}
                </FieldValue>
              </Field>
            </FieldGrid>
          </Section>

          <Section>
            <SectionTitle>{t('admin.courses.description')}</SectionTitle>
            {course.content ? (
              <Description
                // biome-ignore lint/security/noDangerouslySetInnerHtml: admin-authored course HTML
                dangerouslySetInnerHTML={{ __html: course.content }}
              />
            ) : (
              <Empty>{t('admin.courses.noDescription')}</Empty>
            )}
          </Section>

          <Section>
            <SectionTitle>{t('admin.courses.images')}</SectionTitle>
            <Images>
              <Field>
                <FieldLabel as="div">
                  {t('admin.courses.coverImage')}
                </FieldLabel>
                {course.cover_image ? (
                  <Frame $wide>
                    <img
                      src={course.cover_image}
                      alt={t('admin.courses.coverAlt', {
                        title: course.title,
                        interpolation: { escapeValue: false },
                      })}
                    />
                  </Frame>
                ) : (
                  <FieldValue>{t('admin.courses.noCover')}</FieldValue>
                )}
              </Field>
              <Field>
                <FieldLabel as="div">{t('admin.courses.thumbnail')}</FieldLabel>
                {course.thumbnail ? (
                  <Frame>
                    <img
                      src={course.thumbnail}
                      alt={t('admin.courses.thumbnailAlt', {
                        title: course.title,
                        interpolation: { escapeValue: false },
                      })}
                    />
                  </Frame>
                ) : (
                  <FieldValue>{t('admin.courses.noThumbnail')}</FieldValue>
                )}
              </Field>
            </Images>
          </Section>

          <Section>
            <SectionTitle>{t('admin.series.certificate')}</SectionTitle>
            <CertificateRow>
              <div>
                <FieldValue>
                  {certificateTitle !== null ? (
                    <>
                      {t('admin.courses.certificateEarn')}{' '}
                      <CertificateName>{certificateTitle}</CertificateName>.
                    </>
                  ) : (
                    t('admin.courses.noCertificate')
                  )}
                </FieldValue>
              </div>
              <CertificateTemplateControl
                owner={{ type: 'course', id: course.id }}
                hasTemplate={certificateTitle !== null}
                backfillable={course.certificate_backfillable}
                defaultTitle={course.title}
                onChanged={() => invalidateCourse(course.id, course.version)}
              />
            </CertificateRow>
          </Section>

          <Section>
            <SectionTitle>
              {t('admin.content.translations.heading')}
            </SectionTitle>
            <TranslationLinksPanel
              kind="course"
              id={course.id}
              language={course.language}
              translations={course.translations}
              editable
              onChanged={() => invalidateCourse(course.id, course.version)}
            />
          </Section>
        </CourseOutlineLayout>
      )}
    </DetailPage>
  );
}

function CourseEditor({
  course,
  onDone,
}: {
  course: CourseAdmin;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(course.title);
  const [slug, setSlug] = useState(course.slug);
  const [order, setOrder] = useState(String(course.order));
  const [status, setStatus] = useState(course.status);
  const [content, setContent] = useState(course.content);
  const [thumbnail, setThumbnail] = useState({
    key: course.thumbnail_key,
    url: course.thumbnail,
  });
  const [cover, setCover] = useState({
    key: course.cover_image_key,
    url: course.cover_image,
  });
  // One flag per picker: either one uploading holds the save.
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const uploading = thumbnailUploading || coverUploading;
  const [confirming, setConfirming] = useState(false);
  const [confirmingIntro, setConfirmingIntro] = useState(false);
  const [helperRequired, setHelperRequired] = useState(
    course.study_helper_required,
  );
  const [unlockedByHelper, setUnlockedByHelper] = useState(
    course.unlocked_by_study_helper,
  );
  const [notRestricted, setNotRestricted] = useState(
    course.unlocked_not_restricted,
  );
  const [isIntro, setIsIntro] = useState(course.is_intro);
  const [prerequisite, setPrerequisite] = useState<PickedContent | null>(
    course.prerequisite_course
      ? {
          id: course.prerequisite_course.id,
          title: course.prerequisite_course.title,
        }
      : null,
  );
  const [requiredSeries, setRequiredSeries] = useState<PickedContent[]>(
    course.series_completion_required.map((r) => ({
      id: r.id,
      title: r.title,
    })),
  );

  const formValues = JSON.stringify([
    title,
    slug,
    order,
    status,
    content,
    thumbnail.key,
    cover.key,
    helperRequired,
    unlockedByHelper,
    notRestricted,
    isIntro,
    prerequisite?.id ?? null,
    requiredSeries.map((r) => r.id),
  ]);
  const [savedValues] = useState(formValues);
  const dirty = formValues !== savedValues;
  const {
    allowLeave,
    confirmDiscard,
    dialog: unsavedDialog,
  } = useUnsavedChanges(dirty);

  const invalidate = () => {
    invalidateCourse(course.id, course.version);
    invalidateCourseVersions(course.id);
  };

  const updateMutation = api.adminUpdateCourse.useMutation(undefined, {
    onSuccess: () => {
      allowLeave();
      invalidate();
      onDone();
    },
  });

  const navigate = useNavigate();
  const deleteMutation = api.adminDeleteCourse.useMutation(undefined, {
    meta: { toast: t('admin.common.deleted') },
    onSuccess: () => {
      allowLeave();
      queryClient.removeQueries({
        queryKey: api.adminGetCourse.getQueryKey({
          path: { course_id: course.id },
        }),
      });
      queryClient.invalidateQueries({
        queryKey: api.adminListCourses.getQueryKey({}),
      });
      navigate({ to: '/admin/courses/' });
    },
  });

  const trimmedTitle = title.trim();
  // A published version is frozen structurally: order drives next-course
  // progression and slug is a live student URL, so the server rejects changing
  // them (or unpublishing). Those changes go in a new version.
  const isPublished = course.status === 'published';
  // A draft of a live course goes live from Review changes, not from here.
  const isDraftVersion = !isPublished && course.live_version != null;
  const willPublish = !isPublished && status === 'published';

  // Pinned when editing starts: a refetch (e.g. on tab focus) must not advance it,
  // or a save over someone else's change would slip past the conflict check.
  const [baseRevision] = useState(course.revision);
  const becomesIntro = isIntro && !course.is_intro;
  const originalRequired = course.series_completion_required.map((r) => r.id);
  const requiredIds = requiredSeries.map((r) => r.id);
  const requiredChanged =
    JSON.stringify(requiredIds) !== JSON.stringify(originalRequired);
  const prerequisiteChanged =
    (prerequisite?.id ?? null) !== (course.prerequisite_course?.id ?? null);

  // Each confirmation re-enters with what the editor already agreed to.
  const save = (publishOk: boolean, introOk: boolean) => {
    if (!trimmedTitle || updateMutation.isPending || uploading) return;
    if (willPublish && !publishOk) {
      setConfirming(true);
      return;
    }
    if (becomesIntro && !introOk) {
      setConfirmingIntro(true);
      return;
    }
    updateMutation.mutate({
      path: { course_id: course.id },
      query: { version: course.version },
      body: {
        title: trimmedTitle,
        slug,
        order: Number(order),
        status,
        // Only when edited, so other saves don't re-store the description.
        ...(content !== course.content ? { content } : {}),
        thumbnail_key: thumbnail.key,
        cover_image_key: cover.key,
        // Settings go only when changed: is_intro clears another course's flag.
        ...(helperRequired !== course.study_helper_required
          ? { study_helper_required: helperRequired }
          : {}),
        ...(unlockedByHelper !== course.unlocked_by_study_helper
          ? { unlocked_by_study_helper: unlockedByHelper }
          : {}),
        ...(notRestricted !== course.unlocked_not_restricted
          ? { unlocked_not_restricted: notRestricted }
          : {}),
        ...(isIntro !== course.is_intro ? { is_intro: isIntro } : {}),
        // 0 clears the prerequisite: an explicit null is dropped server-side.
        ...(prerequisiteChanged
          ? { prerequisite_course_id: prerequisite?.id ?? 0 }
          : {}),
        ...(requiredChanged ? { series_completion_required: requiredIds } : {}),
        expected_revision: baseRevision,
      },
    });
  };
  const handleSave = () => save(false, false);

  const settingsRefusal = updateMutation.error
    ? refusalKey(updateMutation.error, COURSE_SETTINGS_REFUSALS)
    : null;

  const reload = () => {
    allowLeave();
    invalidate();
    onDone();
  };

  return (
    <EditorLayout>
      <EditorMain>
        <LiveEditNotice state={course.version_state} />
        <FormSection
          tone="content"
          icon={faFileLines}
          title={t('admin.common.details')}
          description={t('admin.courses.detailsDescription')}
        >
          <FormRow>
            <FormLabel htmlFor="course-title">
              {t('admin.common.title')}
            </FormLabel>
            <TextInput
              id="course-title"
              value={title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTitle(e.target.value)
              }
            />
          </FormRow>
          <FormRow>
            <FormLabel htmlFor="course-slug">
              {t('admin.common.slug')}
            </FormLabel>
            <TextInput
              id="course-slug"
              value={slug}
              disabled={isPublished}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSlug(e.target.value)
              }
            />
          </FormRow>
        </FormSection>

        <FormSection
          tone="feedback"
          icon={faBookOpen}
          title={t('admin.courses.description')}
          description={t('admin.courses.descriptionDescription')}
        >
          <RichTextEditor
            value={course.content}
            onChange={setContent}
            ariaLabel={t('admin.courses.descriptionAria')}
          />
        </FormSection>

        <FormSection
          tone="settings"
          icon={faLockOpen}
          title={t('admin.courses.access')}
          description={t('admin.courses.accessDescription')}
        >
          <FormRow>
            <CheckboxLabel htmlFor="course-helper-required">
              <input
                id="course-helper-required"
                type="checkbox"
                checked={helperRequired}
                onChange={(e) => setHelperRequired(e.target.checked)}
              />
              {t('admin.courses.studyHelperRequired')}
            </CheckboxLabel>
            <FieldHint>{t('admin.courses.studyHelperRequiredHint')}</FieldHint>
          </FormRow>
          <FormRow>
            <CheckboxLabel htmlFor="course-unlocked-by-helper">
              <input
                id="course-unlocked-by-helper"
                type="checkbox"
                checked={unlockedByHelper}
                onChange={(e) => setUnlockedByHelper(e.target.checked)}
              />
              {t('admin.courses.unlockedByStudyHelper')}
            </CheckboxLabel>
            <FieldHint>
              {t('admin.courses.unlockedByStudyHelperHint')}
            </FieldHint>
          </FormRow>
          <FormRow>
            <CheckboxLabel htmlFor="course-not-restricted">
              <input
                id="course-not-restricted"
                type="checkbox"
                checked={notRestricted}
                onChange={(e) => setNotRestricted(e.target.checked)}
              />
              {t('admin.courses.unlockedNotRestricted')}
            </CheckboxLabel>
            <FieldHint>
              {t('admin.courses.unlockedNotRestrictedHint')}
            </FieldHint>
          </FormRow>
          <FormRow>
            <CheckboxLabel htmlFor="course-is-intro">
              <input
                id="course-is-intro"
                type="checkbox"
                checked={isIntro}
                onChange={(e) => setIsIntro(e.target.checked)}
              />
              {t('admin.courses.isIntro')}
            </CheckboxLabel>
            <FieldHint>{t('admin.courses.isIntroHint')}</FieldHint>
          </FormRow>
          <FormRow>
            <FormLabel htmlFor="course-prerequisite">
              {t('admin.courses.prerequisite')}
            </FormLabel>
            <FieldHint>{t('admin.courses.prerequisiteHint')}</FieldHint>
            {prerequisite && (
              <ChipList>
                <Chip>
                  {prerequisite.title}
                  <button
                    type="button"
                    aria-label={t('admin.common.remove')}
                    onClick={() => setPrerequisite(null)}
                  >
                    ×
                  </button>
                </Chip>
              </ChipList>
            )}
            <ContentPicker
              id="course-prerequisite"
              kind="course"
              label={t('admin.courses.prerequisite')}
              language={course.language}
              excludeIds={[course.id]}
              onPick={setPrerequisite}
            />
          </FormRow>
          <FormRow>
            <FormLabel htmlFor="course-required-series">
              {t('admin.courses.requiredSeries')}
            </FormLabel>
            <FieldHint>{t('admin.courses.requiredSeriesHint')}</FieldHint>
            {requiredSeries.length > 0 && (
              <ChipList>
                {requiredSeries.map((r) => (
                  <Chip key={r.id}>
                    {r.title}
                    <button
                      type="button"
                      aria-label={t('admin.courses.removeNamed', {
                        title: r.title,
                        interpolation: { escapeValue: false },
                      })}
                      onClick={() =>
                        setRequiredSeries((cur) =>
                          cur.filter((x) => x.id !== r.id),
                        )
                      }
                    >
                      ×
                    </button>
                  </Chip>
                ))}
              </ChipList>
            )}
            <ContentPicker
              id="course-required-series"
              kind="series"
              label={t('admin.courses.requiredSeries')}
              language={course.language}
              excludeIds={requiredIds}
              onPick={(item) => setRequiredSeries((cur) => [...cur, item])}
            />
          </FormRow>
          <FormRow>
            <FormLabel as="div">{t('admin.courses.accessMode')}</FormLabel>
            <InfoNotice>
              {course.access_mode === 'closed'
                ? t('admin.courses.accessClosedInfo')
                : t('admin.courses.accessOpenInfo')}
            </InfoNotice>
          </FormRow>
        </FormSection>

        <FormSection
          tone="answers"
          icon={faImage}
          title={t('admin.courses.images')}
          description={t('admin.courses.imagesDescription')}
        >
          <FormRow>
            <FormLabel htmlFor="course-cover">
              {t('admin.courses.coverImage')}
            </FormLabel>
            <FieldHint>{t('admin.courses.coverHint')}</FieldHint>
            <MediaPicker
              id="course-cover"
              value={cover}
              onChange={setCover}
              onUploadingChange={setCoverUploading}
              alt={t('admin.courses.coverImageAlt')}
              wide
            />
          </FormRow>
          <FormRow>
            <FormLabel htmlFor="course-thumbnail">
              {t('admin.courses.thumbnail')}
            </FormLabel>
            <FieldHint>{t('admin.courses.thumbnailHint')}</FieldHint>
            <MediaPicker
              id="course-thumbnail"
              value={thumbnail}
              onChange={setThumbnail}
              onUploadingChange={setThumbnailUploading}
              alt={t('admin.courses.thumbnailAltInput')}
            />
          </FormRow>
        </FormSection>
      </EditorMain>

      <EditorPanel aria-label={t('admin.series.saveAndSettings')}>
        <SaveGroup
          heading={<PanelLabel>{t('admin.courses.panelLabel')}</PanelLabel>}
          dirty={dirty}
          saving={updateMutation.isPending}
          saveDisabled={uploading || !trimmedTitle}
          onSave={handleSave}
          onCancel={() => confirmDiscard(onDone)}
        >
          {!trimmedTitle && (
            <FieldHint>{t('admin.courses.giveTitle')}</FieldHint>
          )}
          {updateMutation.isError &&
            (editConflict(updateMutation.error) ? (
              <EditConflictNotice
                error={updateMutation.error}
                onReload={reload}
              />
            ) : (
              <ErrorText>
                {settingsRefusal
                  ? t(settingsRefusal)
                  : (apiErrorDetail(updateMutation.error) ??
                    t('admin.common.couldNotSave'))}
              </ErrorText>
            ))}
        </SaveGroup>

        <PanelGroup>
          <FormRow>
            <FormLabel htmlFor="course-status">
              {t('admin.common.status')}
            </FormLabel>
            {isPublished ? (
              <InfoNotice>{t('admin.courses.publishedInfo')}</InfoNotice>
            ) : isDraftVersion ? (
              <InfoNotice>
                {t('admin.courses.draftOfLive')}{' '}
                <Link
                  to="/admin/courses/$courseId/$version/changes/"
                  params={{
                    courseId: String(course.id),
                    version: String(course.version),
                  }}
                >
                  {t('admin.contentNav.reviewChanges')}
                </Link>
                .
              </InfoNotice>
            ) : (
              <>
                <Select
                  id="course-status"
                  value={status}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setStatus(e.target.value as CourseAdmin['status'])
                  }
                >
                  <option value="draft">draft</option>
                  <option value="published">published</option>
                </Select>
                {willPublish && (
                  <WarningNotice>
                    {t('admin.courses.publishWarning')}
                  </WarningNotice>
                )}
              </>
            )}
            <DraftAncestorNotice
              entityLabel="course"
              ancestors={[
                {
                  label: t('admin.entity.series'),
                  status: course.series_status,
                },
              ]}
            />
          </FormRow>
          <FormRow>
            <FormLabel htmlFor="course-order">
              {t('admin.common.order')}
            </FormLabel>
            <TextInput
              id="course-order"
              type="number"
              value={order}
              disabled={isPublished}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setOrder(e.target.value)
              }
            />
          </FormRow>
        </PanelGroup>

        {/* A draft of a live course is discarded from its version menu instead. */}
        {!isDraftVersion && (
          <DeleteContent
            noun="course"
            blocker={course.delete_blocker}
            pending={deleteMutation.isPending}
            error={deleteMutation.error}
            onDelete={() =>
              deleteMutation.mutate({ path: { course_id: course.id } })
            }
          >
            <p>{t('admin.courses.deleteCascade')}</p>
          </DeleteContent>
        )}
      </EditorPanel>

      <ConfirmDialog
        open={confirming}
        title={t('admin.series.confirmTitle')}
        confirmLabel={t('admin.common.publish')}
        onConfirm={() => {
          setConfirming(false);
          save(true, false);
        }}
        onCancel={() => setConfirming(false)}
      >
        <p>{t('admin.courses.confirmBody1')}</p>
        <p>{t('admin.courses.confirmBody2')}</p>
      </ConfirmDialog>
      <ConfirmDialog
        open={confirmingIntro}
        title={t('admin.courses.introConfirmTitle')}
        confirmLabel={t('admin.courses.introConfirm')}
        onConfirm={() => {
          setConfirmingIntro(false);
          save(true, true);
        }}
        onCancel={() => setConfirmingIntro(false)}
      >
        <p>{t('admin.courses.introConfirmBody')}</p>
      </ConfirmDialog>
      {unsavedDialog}
    </EditorLayout>
  );
}

function YesNo({ label, value }: { label: string; value: boolean }) {
  const { t } = useTranslation();
  return (
    <Field>
      <FieldLabel>{label}</FieldLabel>
      <FieldValue>
        {value ? t('admin.common.yes') : t('admin.common.no')}
      </FieldValue>
    </Field>
  );
}

const HeaderActions = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 0.5rem;
`;

const Description = styled(RichContent)`
  padding: 0 var(--inner-padding) var(--inner-padding);
`;

const Empty = styled.p`
  color: var(--grey-600);
  margin: 0;
  padding: 0 var(--inner-padding) var(--inner-padding);
`;

const Images = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--component-margin);
  padding: 0 var(--inner-padding) var(--inner-padding);
`;

const CertificateName = styled.strong`
  color: var(--grey-900);
  font-weight: 600;
`;

const CertificateRow = styled.div`
  align-items: center;
  display: flex;
  gap: var(--component-margin);
  justify-content: space-between;
  padding: var(--inner-padding);
`;

const Message = styled.div<{ $error?: boolean }>`
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
  padding: 2rem;
  text-align: center;
`;
